import type { Href } from "expo-router";
import { Stack, usePathname, useRouter } from "expo-router";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { ActivityIndicator, View } from "react-native";

import { getMfaPortalState, nonAdminMfaEnrollHref } from "./adminMfaRouting";
import { isAdminRole, portalAllowsRole, roleToRoute, type PortalKey } from "./auth";
import { useAuth } from "./authContext";
import { COLORS } from "./brand";
import { impersonationPortalKey } from "./impersonate";
import { isClientCaseEditPath } from "./adminCaseEdit";

/**
 * Admin session UX (pending employees): profile onboarding -> TOTP enrollment -> step-up code on each new session ->
 * `/admin/pending` until approved. Active admins skip profile onboarding. (`/onboard/employee` is outside this layout.)
 */
const ADMIN_EMPLOYEE_EXCEPTION_PATHS = ["/admin/pending", "/admin/rejected", "/admin/mfa/enroll", "/admin/security"];

function isAdminEmployeeExceptionPath(pathname: string): boolean {
  return ADMIN_EMPLOYEE_EXCEPTION_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function isAdminRejectedPath(pathname: string): boolean {
  return pathname === "/admin/rejected" || pathname.startsWith("/admin/rejected/");
}

function isAdminMfaEnrollPath(pathname: string): boolean {
  return pathname === "/admin/mfa/enroll" || pathname.startsWith("/admin/mfa/enroll/");
}

export function PortalGate({ portal, children }: { portal: PortalKey; children?: ReactNode }) {
  const { user, role, employeeStatus, mfaRequired, loading } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  // Renamed from `adminMfaOk` - now applies to any role. Keeps the admin
  // pending/rejected flows untouched while letting clients/students/etc opt in
  // to MFA via Settings -> Security.
  const [mfaOk, setMfaOk] = useState(false);
  const redirecting = useRef(false);
  const lastVerifiedRef = useRef<{ pathname: string; userId: string | null } | null>(null);

  useEffect(() => {
    if (loading) return;
    if (!user || !role) {
      // Suppress the /login redirect when an explicit sign-out is in
      // flight — signOutToHome sets this flag right before kicking off a
      // hard navigation to "/", so any in-app redirect here would just
      // flash the sign-in page in front of the user.
      if (
        typeof window !== "undefined" &&
        window.sessionStorage?.getItem("coravu_signing_out") === "1"
      ) {
        return;
      }
      router.replace("/login" as Href);
      return;
    }
    // "View as": a super_admin who has entered another portal's view is
    // allowed to render it. Only ever relaxes for an actual admin role — a
    // non-admin faking the client signal gains nothing (RLS still scopes every
    // read to themselves; the server session + edit RPCs require super_admin).
    const viewingHere = isAdminRole(role) && impersonationPortalKey() === portal;
    // Admin case-edit escape hatch: an admin may render the client case editor
    // (+ its deliverable sub-routes) to fix a submitted case. Narrow to those
    // paths; admin RLS authorizes the writes.
    const adminCaseEditing =
      isAdminRole(role) && portal === "client" && isClientCaseEditPath(pathname);
    if (!portalAllowsRole(portal, role) && !viewingHere && !adminCaseEditing) {
      router.replace(roleToRoute(role) as Href);
      return;
    }

    if (portal === "admin" && isAdminRole(role)) {
      if (employeeStatus === "rejected" && !isAdminRejectedPath(pathname)) {
        router.replace("/admin/rejected" as Href);
        return;
      }

      if (employeeStatus === "pending") {
        const onboardDone = user.user_metadata?.employee_onboard_completed === true;
        if (!onboardDone && pathname !== "/onboard/employee" && !pathname.startsWith("/onboard/employee")) {
          router.replace("/onboard/employee" as Href);
          return;
        }
      }

      if (employeeStatus === "pending" && !isAdminEmployeeExceptionPath(pathname)) {
        router.replace("/admin/pending" as Href);
        return;
      }
    }
  }, [loading, user, role, employeeStatus, portal, router, pathname]);

  useEffect(() => {
    redirecting.current = false;
    if (loading || !user || !role) {
      if (!user) lastVerifiedRef.current = null;
      setMfaOk(true);
      return;
    }

    // A super_admin viewing-as into another portal — or an admin rendering the
    // client case editor — has already cleared their own MFA on the admin
    // session; don't re-challenge them in the client portal.
    if (
      isAdminRole(role) &&
      (impersonationPortalKey() === portal ||
        (portal === "client" && isClientCaseEditPath(pathname)))
    ) {
      setMfaOk(true);
      return;
    }

    // Admins in the rejected/enroll paths bypass the gate (they need to be
    // able to view those screens specifically).
    if (portal === "admin" && isAdminRole(role)) {
      if (isAdminRejectedPath(pathname) || isAdminMfaEnrollPath(pathname)) {
        setMfaOk(true);
        return;
      }
    }

    // For non-admin portals: when the user lands on their own settings page,
    // don't bounce them - that's where they go to either complete enrollment
    // (recovery) or toggle MFA on/off. Without this, an `enroll`-state
    // user with MFA toggled on but factor missing would loop.
    const isOwnSettingsPath =
      portal !== "admin" &&
      (pathname === `/${portal}/settings` || pathname.startsWith(`/${portal}/settings/`));
    if (isOwnSettingsPath) {
      setMfaOk(true);
      return;
    }

    if (!mfaRequired) {
      setMfaOk(true);
      return;
    }

    if (lastVerifiedRef.current?.userId === user.id) {
      setMfaOk(true);
      return;
    }

    setMfaOk(false);
    let cancelled = false;

    void (async () => {
      const state = await getMfaPortalState({ role, mfaRequired });
      if (cancelled || redirecting.current) return;
      if (state === "enroll") {
        redirecting.current = true;
        // Admins go to the dedicated enroll flow; others go back to their
        // Settings page where the toggle / re-enroll lives.
        router.replace(
          portal === "admin" && isAdminRole(role)
            ? ("/admin/mfa/enroll" as Href)
            : nonAdminMfaEnrollHref(portal)
        );
        return;
      }
      if (state === "challenge") {
        redirecting.current = true;
        router.replace("/login/mfa-challenge" as Href);
        return;
      }
      lastVerifiedRef.current = { pathname, userId: user.id };
      setMfaOk(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [loading, user, role, portal, pathname, mfaRequired]);

  const viewingHere = !!role && isAdminRole(role) && impersonationPortalKey() === portal;
  const adminCaseEditing =
    !!role && isAdminRole(role) && portal === "client" && isClientCaseEditPath(pathname);
  const base =
    !!user && !!role && (portalAllowsRole(portal, role) || viewingHere || adminCaseEditing);
  let allowed = base;
  if (base && portal === "admin" && isAdminRole(role) && employeeStatus === "pending") {
    allowed = isAdminEmployeeExceptionPath(pathname) && mfaOk;
  } else if (base && portal === "admin" && isAdminRole(role) && employeeStatus === "rejected") {
    allowed = isAdminRejectedPath(pathname);
  } else if (base) {
    // Generalized: any portal - once base allowance is true, we additionally
    // require the user to have completed any required MFA challenge for this
    // session. Non-admin portals where mfaRequired === false will short-circuit
    // mfaOk to true above, so this is a no-op for opted-out users.
    allowed = mfaOk;
  }

  if (loading || !allowed) {
    // Spinner only while the auth context itself is loading. After that,
    // a disallowed render means a redirect is already in flight (MFA
    // challenge/enroll bounce, wrong-portal swap, pending-employee hold) —
    // show just the quiet background so the user goes straight to the
    // destination with no loading-bar flash in between.
    return (
      <View className="flex-1 items-center justify-center bg-bg">
        {loading ? <ActivityIndicator size="large" color={COLORS.FOREST.hex} /> : null}
      </View>
    );
  }

  return children ?? <Stack screenOptions={{ headerShown: false }} />;
}
